import { Alert, Col, Row } from "antd";
import { useContext, useEffect, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import AuthContext from "../../../context/auth/context";
import AppContext from "../../../context/app/context";
import { IShoppingDaypart } from "../../../models/general-setting/response";
import {
  CustomerSatisfactionStatus,
  getCustomerSatisfactionStatusLabel,
} from "../../../models/customer-satisfaction/enum";
import { getProductStatus } from "../../../models/product/enum";
import { getReportType, ReportType } from "../../../models/report/enum";
import EndPoints from "../../../services/end-points";
import { Permissions } from "../../../utils/Permissions/Permissions";
import {
  getShoppingDaypartLabel,
  getShoppingDaypartsValue,
} from "../../../utils/helpers/shopping-dayparts";
import FieldBuilder from "../../form-components/field-builder";

const DATE_FORMAT = "YYYY-MM-DD";

const Form = () => {
  const { t, i18n } = useTranslation();

  const [deliveryWorkers, setDeliveryWorkers] = useState<any[]>();
  const [deliveryWorkersLoading, setDeliveryWorkersLoading] = useState(true);

  const [stores, setStores] = useState<any[]>();
  const [storesLoading, setStoresLoading] = useState(true);

  const [areas, setAreas] = useState<any[]>([]);
  const [areasLoading, setAreasLoading] = useState(false);

  const [discountAreas, setDiscountAreas] = useState<any[]>([]);
  const [discountAreasLoading, setDiscountAreasLoading] = useState(false);

  const [dissatisfactionReasons, setDissatisfactionReasons] = useState<any[]>(
    [],
  );
  const [dissatisfactionReasonsLoading, setDissatisfactionReasonsLoading] =
    useState(false);

  const [trademarks, setTrademarks] = useState<any[]>();
  const [trademarksLoading, setTrademarksLoading] = useState(true);

  const [discountCodes, setDiscountCodes] = useState<any[]>();
  const [discountCodesLoading, setDiscountCodesLoading] = useState(true);

  const [categories, setCategories] = useState<any[]>();
  const [categoriesLoading, setCategoriesLoading] = useState(false);

  const [shoppingDayparts, setShoppingDayparts] = useState<IShoppingDaypart[]>(
    [],
  );
  const [shoppingDaypartsLoading, setShoppingDaypartsLoading] = useState(false);

  const { direction } = useContext(AppContext);
  const { userPermissions: permissions } = useContext(AuthContext);

  let userPermissions =
    permissions?.length > 0
      ? permissions
      : localStorage?.getItem("permisionStoroge")?.split(",");
  if (!userPermissions) userPermissions = [];

  const hasPermission = (permission: string) =>
    userPermissions.includes(permission);
  const canExportProductMarginReport =
    hasPermission(Permissions.CAN_EXPORT_PRODUCT_MARGIN_REPORT) &&
    hasPermission(Permissions.CAN_MANAGE_PURCHASE_PRICE);
  const canExportCouponImpactReport = hasPermission(
    Permissions.CAN_EXPORT_COUPON_IMPACT_REPORT,
  );

  const type = [
    {
      type: ReportType.PHARMACIES_ORDERS,
      canExport: hasPermission(Permissions.CAN_EXPORT_PHARMACIES_ORDERS_REPORT),
    },
    {
      type: ReportType.ORDER_USER_DETAILS,
      canExport: hasPermission(
        Permissions.CAN_EXPORT_ORDER_USER_DETAILS_REPORT,
      ),
    },
    {
      type: ReportType.ORDERS_CANCELLATION,
      canExport: hasPermission(
        Permissions.CAN_EXPORT_ORDER_CANCELLATION_REPORT,
      ),
    },
    {
      type: ReportType.DRIVERS_PERFORMANCE,
      canExport: hasPermission(
        Permissions.CAN_EXPORT_DRIVER_PERFORMANCE_REPORT,
      ),
    },
    {
      type: ReportType.ORDERS_DAYLE_INFO,
      canExport: hasPermission(Permissions.CAN_EXPORT_ORDERS_DAYLE_INFO_REPORT),
    },
    {
      type: ReportType.ORDERS_STATUS,
      canExport: hasPermission(Permissions.CAN_EXPORT_ORDER_STATUS_REPORT),
    },
    {
      type: ReportType.PAID_ORDERS_JOURNEY,
      canExport: hasPermission(
        Permissions.CAN_EXPORT_PAID_ORDERS_JOURNEY_REPORT,
      ),
    },
    {
      type: ReportType.ORDERS_DETAILS,
      canExport: hasPermission(Permissions.CAN_EXPORT_ORDERS_DETAILS_REPORT),
    },
    {
      type: ReportType.DRIVER_PAYMENTS,
      canExport: hasPermission(Permissions.CAN_EXPORT_ORDERS_DETAILS_REPORT),
    },
    {
      type: ReportType.USER_ACTIVITY_REPORT,
      canExport: hasPermission(Permissions.CAN_EXPORT_USER_ACTIVITY_REPORT),
    },
    {
      type: ReportType.MONTHLY_USER_ACTIVITY_REPORT,
      canExport: hasPermission(
        Permissions.CAN_EXPORT_MONTHLY_USER_ACTIVITY_REPORT,
      ),
    },
    {
      type: ReportType.LOW_STOCK_REPORT,
      canExport: hasPermission(Permissions.CAN_EXPORT_LOW_STOCK_REPORT),
    },
    {
      type: ReportType.PRODUCT_BATCH_STOCK_REPORT,
      canExport: hasPermission(
        Permissions.CAN_EXPORT_PRODUCT_BATCH_STOCK_REPORT,
      ),
    },
    {
      type: ReportType.PRODUCT_MARGIN_REPORT,
      canExport: canExportProductMarginReport,
    },
    {
      type: ReportType.SECTOR_DELIVERY_TIME_REPORT,
      canExport: hasPermission(
        Permissions.CAN_EXPORT_SECTOR_DELIVERY_TIME_REPORT,
      ),
    },
    {
      type: ReportType.STORE_ACCEPTANCE_TIME_REPORT,
      canExport: hasPermission(
        Permissions.CAN_EXPORT_STORE_ACCEPTANCE_TIME_REPORT,
      ),
    },
    {
      type: ReportType.PREPARING_TIME_REPORT,
      canExport: hasPermission(Permissions.CAN_EXPORT_PREPARING_TIME_REPORT),
    },
    {
      type: ReportType.ORDER_CYCLE_TIME_REPORT,
      canExport: hasPermission(Permissions.CAN_EXPORT_ORDER_CYCLE_TIME_REPORT),
    },
    {
      type: ReportType.MODIFIED_ORDER_ACCEPTANCE_TIME_REPORT,
      canExport: hasPermission(
        Permissions.CAN_EXPORT_MODIFIED_ORDER_ACCEPTANCE_TIME_REPORT,
      ),
    },
    {
      type: ReportType.ACCOUNTING_FINALIZATION_TIME_REPORT,
      canExport: hasPermission(
        Permissions.CAN_EXPORT_ACCOUNTING_FINALIZATION_TIME_REPORT,
      ),
    },
    {
      type: ReportType.DRIVER_SEARCH_TIME_REPORT,
      canExport: hasPermission(
        Permissions.CAN_EXPORT_DRIVER_SEARCH_TIME_REPORT,
      ),
    },
    {
      type: ReportType.CUSTOMER_CANCELLATION_REPORT,
      canExport: hasPermission(
        Permissions.CAN_EXPORT_CUSTOMER_CANCELLATION_REPORT,
      ),
    },
    {
      type: ReportType.ADMINISTRATIVE_CANCELLATION_REPORT,
      canExport: hasPermission(
        Permissions.CAN_EXPORT_ADMINISTRATIVE_CANCELLATION_REPORT,
      ),
    },
    {
      type: ReportType.CUSTOMER_SATISFACTION_REPORT,
      canExport: hasPermission(
        Permissions.CAN_EXPORT_CUSTOMER_SATISFACTION_REPORT,
      ),
    },
    {
      type: ReportType.CUSTOMER_GEOGRAPHIC_DISTRIBUTION_REPORT,
      canExport: hasPermission(
        Permissions.CAN_EXPORT_CUSTOMER_GEOGRAPHIC_DISTRIBUTION_REPORT,
      ),
    },
    {
      type: ReportType.COUPON_IMPACT,
      canExport: canExportCouponImpactReport,
    },
    {
      type: ReportType.CART_BEHAVIOR_REPORT,
      canExport: hasPermission(Permissions.CAN_EXPORT_CART_BEHAVIOR_REPORT),
    },
    {
      type: ReportType.CATEGORY_SALES_TIMING_REPORT,
      canExport: hasPermission(
        Permissions.CAN_EXPORT_CATEGORY_SALES_TIMING_REPORT,
      ),
    },
    {
      type: ReportType.RFM_REPORT,
      canExport: hasPermission(Permissions.CAN_EXPORT_RFM_REPORT),
    },
  ]
    .filter((item) => item.canExport)
    .map((item) => item.type);

  const { control, setValue, getValues } = useFormContext();

  const typeWatch = useWatch({
    control,
    name: "type",
  });

  const startDateWatch = useWatch({
    control,
    name: "startDate",
  });

  const endDateWatch = useWatch({
    control,
    name: "endDate",
  });

  const historyStartDateWatch = useWatch({
    control,
    name: "historyStartDate",
  });

  const clientCreatedStartDateWatch = useWatch({
    control,
    name: "clientCreatedStartDate",
  });

  const orderStartDateWatch = useWatch({
    control,
    name: "orderStartDate",
  });

  const hasDiscountCodeWatch = useWatch({
    control,
    name: "hasDiscountCode",
  });

  const isLowStock = typeWatch === ReportType.LOW_STOCK_REPORT;
  const isProductMargin = typeWatch === ReportType.PRODUCT_MARGIN_REPORT;
  const isProductBatchStock =
    typeWatch === ReportType.PRODUCT_BATCH_STOCK_REPORT;
  const isCouponImpact = typeWatch === ReportType.COUPON_IMPACT;
  const isShoppingCartBehavior = typeWatch === ReportType.CART_BEHAVIOR_REPORT;
  const isCategorySalesTiming =
    typeWatch === ReportType.CATEGORY_SALES_TIMING_REPORT;
  const isCustomerSatisfactionReport =
    typeWatch === ReportType.CUSTOMER_SATISFACTION_REPORT;
  const isCustomerGeographicDistributionReport =
    typeWatch === ReportType.CUSTOMER_GEOGRAPHIC_DISTRIBUTION_REPORT;
  const isRfm = typeWatch === ReportType.RFM_REPORT;
  const needsCategoryAndDaypartOptions =
    isShoppingCartBehavior || isCategorySalesTiming;
  const showLongReportRangeNote =
    isRfm ||
    isShoppingCartBehavior ||
    isCategorySalesTiming ||
    isCustomerGeographicDistributionReport;
  const shouldLoadDiscountCodes =
    canExportCouponImpactReport || isCustomerSatisfactionReport;
  const shouldLoadAreas =
    isCustomerSatisfactionReport || isCustomerGeographicDistributionReport;
  const shouldLoadStores =
    isRfm ||
    isCategorySalesTiming ||
    isShoppingCartBehavior ||
    isCustomerSatisfactionReport ||
    isLowStock ||
    isProductBatchStock ||
    isProductMargin;
  const shouldLoadTrademarks = isProductMargin;
  const shouldLoadDeliveryWorkers =
    isCustomerSatisfactionReport || typeWatch === ReportType.DRIVER_PAYMENTS;
  const shouldLoadDissatisfactionReasons = isCustomerSatisfactionReport;

  const batchStockFilterModeWatch = useWatch({
    control,
    name: "batchStockFilterMode",
  });

  const couponSelectModeWatch = useWatch({
    control,
    name: "couponSelectMode",
  });

  const cartValueMinWatch = useWatch({
    control,
    name: "cartValueMin",
  });

  const minOrdersPerUserWatch = useWatch({
    control,
    name: "minOrdersPerUser",
  });

  const completionMinutesMinWatch = useWatch({
    control,
    name: "completionMinutesMin",
  });

  const recencyDaysMinWatch = useWatch({
    control,
    name: "recencyDaysMin",
  });

  const frequencyMinWatch = useWatch({
    control,
    name: "frequencyMin",
  });

  const monetaryMinWatch = useWatch({
    control,
    name: "monetaryMin",
  });

  const today = dayjs().format(DATE_FORMAT);
  const isAfterToday = (value?: string) => Boolean(value && value > today);
  const validateNotFutureDate = (value?: string) =>
    !isAfterToday(value) || t("invalid_date");
  const validateReportEndDate = (value?: string) => {
    if (isAfterToday(value)) return t("invalid_date");
    if (startDateWatch && value && value < startDateWatch) {
      return t("end_date_before_start_date");
    }

    return true;
  };
  const validateEndDateAfter = (startValue?: string) => (value?: string) => {
    if (isAfterToday(value)) return t("invalid_date");
    if (startValue && value && value < startValue) {
      return t("end_date_before_start_date");
    }

    return true;
  };
  const hasFormValue = (value?: any) =>
    value !== undefined && value !== null && value !== "";
  const validateNonNegativeNumber = (value?: any) =>
    !hasFormValue(value) ||
    Number(value) >= 0 ||
    t("value_must_be_zero_or_more");
  const validateMaxNumber = (minValue?: any) => (value?: any) => {
    if (!hasFormValue(value) || !hasFormValue(minValue)) return true;

    return (
      Number(value) >= Number(minValue) || t("max_must_be_greater_than_min")
    );
  };
  const validateCartBehaviorEndDate = (value?: string) => {
    return validateReportEndDate(value);
  };
  const validateCategorySalesTimingEndDate = (value?: string) => {
    return validateReportEndDate(value);
  };
  const validateRfmReportEndDate = (value?: string) => {
    return validateReportEndDate(value);
  };
  const validateRfmHistoryStartDate = (value?: string) => {
    if (!value) return true;
    if (startDateWatch && value > startDateWatch) {
      return t("history_start_date_must_not_be_after_report_start_date");
    }

    return validateNotFutureDate(value);
  };
  const validateRfmHistoryEndDate = (value?: string) => {
    if (!value) return true;
    if (endDateWatch && value < endDateWatch) {
      return t("history_end_date_must_not_be_before_report_end_date");
    }
    if (historyStartDateWatch && value < historyStartDateWatch) {
      return t("history_end_date_before_history_start_date");
    }

    return validateNotFutureDate(value);
  };

  useEffect(() => {
    if (isProductBatchStock && !getValues("batchStockFilterMode")) {
      setValue("batchStockFilterMode", "expiration");
    }
  }, [isProductBatchStock, getValues, setValue]);

  useEffect(() => {
    if (isCouponImpact && !getValues("couponSelectMode")) {
      setValue("couponSelectMode", "select");
    }
  }, [isCouponImpact, getValues, setValue]);

  useEffect(() => {
    if (!isRfm) return;
    if (!getValues("storeFilterMode")) {
      setValue("storeFilterMode", "dominant_store_by_orders");
    }
  }, [isRfm, getValues, setValue]);

  useEffect(() => {
    if (!isCustomerSatisfactionReport) return;
    if (hasDiscountCodeWatch !== false) return;

    setValue("discountCodeIds", undefined);
    setValue("discountCodeId", undefined);
    setValue("discountCode", undefined);
  }, [isCustomerSatisfactionReport, hasDiscountCodeWatch, setValue]);

  useEffect(() => {
    if (!shouldLoadDiscountCodes) {
      setDiscountCodesLoading(false);
      return;
    }

    let isActive = true;

    const getDiscountCodeFilterOptions = async () => {
      setDiscountCodesLoading(true);

      try {
        const { data } = await EndPoints.discountCode.getAllDiscountCode({
          light: true,
          perPage: -1,
        });

        if (!isActive) return;

        setDiscountCodes(data?.data ?? []);
      } catch (error) {
        if (!isActive) return;

        setDiscountCodes([]);
      } finally {
        if (isActive) {
          setDiscountCodesLoading(false);
        }
      }
    };

    getDiscountCodeFilterOptions();

    return () => {
      isActive = false;
    };
  }, [shouldLoadDiscountCodes, direction]);

  useEffect(() => {
    if (!shouldLoadStores) {
      setStoresLoading(false);
      return;
    }

    let isActive = true;

    const getStoreFilterOptions = async () => {
      setStoresLoading(true);

      try {
        const { data } = await EndPoints.store.getAllStores({
          light: true,
          perPage: -1,
        });

        if (!isActive) return;

        setStores(data?.data ?? []);
      } catch (error) {
        if (!isActive) return;

        setStores([]);
      } finally {
        if (isActive) {
          setStoresLoading(false);
        }
      }
    };

    getStoreFilterOptions();

    return () => {
      isActive = false;
    };
  }, [shouldLoadStores, direction]);

  useEffect(() => {
    if (!shouldLoadTrademarks) {
      setTrademarksLoading(false);
      return;
    }

    let isActive = true;

    const getTrademarkFilterOptions = async () => {
      setTrademarksLoading(true);

      try {
        const { data } = await EndPoints.trademark.getAllTrademark({
          light: true,
          perPage: -1,
        });

        if (!isActive) return;

        setTrademarks(data?.data ?? []);
      } catch (error) {
        if (!isActive) return;

        setTrademarks([]);
      } finally {
        if (isActive) {
          setTrademarksLoading(false);
        }
      }
    };

    getTrademarkFilterOptions();

    return () => {
      isActive = false;
    };
  }, [shouldLoadTrademarks, direction]);

  useEffect(() => {
    if (!shouldLoadDeliveryWorkers) {
      setDeliveryWorkersLoading(false);
      return;
    }

    let isActive = true;

    const getDeliveryWorkerFilterOptions = async () => {
      setDeliveryWorkersLoading(true);

      try {
        const { data } = await EndPoints.deliveryWorkers.getAllDeliveryWorkers({
          light: true,
          perPage: -1,
        });

        if (!isActive) return;

        setDeliveryWorkers(data?.data ?? []);
      } catch (error) {
        if (!isActive) return;

        setDeliveryWorkers([]);
      } finally {
        if (isActive) {
          setDeliveryWorkersLoading(false);
        }
      }
    };

    getDeliveryWorkerFilterOptions();

    return () => {
      isActive = false;
    };
  }, [shouldLoadDeliveryWorkers, direction]);

  useEffect(() => {
    if (!shouldLoadDissatisfactionReasons) {
      setDissatisfactionReasonsLoading(false);
      return;
    }

    let isActive = true;

    const getDissatisfactionReasonFilterOptions = async () => {
      setDissatisfactionReasonsLoading(true);

      try {
        const { data } = await EndPoints.customerDissatisfactionReason.getAll({
          light: true,
          perPage: -1,
        });

        if (!isActive) return;

        setDissatisfactionReasons(data?.data ?? []);
      } catch (error) {
        if (!isActive) return;

        setDissatisfactionReasons([]);
      } finally {
        if (isActive) {
          setDissatisfactionReasonsLoading(false);
        }
      }
    };

    getDissatisfactionReasonFilterOptions();

    return () => {
      isActive = false;
    };
  }, [shouldLoadDissatisfactionReasons, direction]);
  useEffect(() => {
    if (!shouldLoadAreas) {
      setAreasLoading(false);
      setDiscountAreasLoading(false);
      return;
    }

    let isActive = true;

    const getAreaFilterOptions = async () => {
      setAreasLoading(true);
      setDiscountAreasLoading(isCustomerGeographicDistributionReport);

      try {
        const [areasResponse, discountAreasResponse] = await Promise.all([
          EndPoints.area.getAllAreas({ light: true, perPage: -1 }),
          isCustomerGeographicDistributionReport
            ? EndPoints.discountarea.getAllAreas({ light: true, perPage: -1 })
            : Promise.resolve(undefined),
        ]);

        if (!isActive) return;

        setAreas(areasResponse.data?.data ?? []);
        if (isCustomerGeographicDistributionReport) {
          setDiscountAreas(discountAreasResponse?.data?.data ?? []);
        }
      } catch (error) {
        if (!isActive) return;

        setAreas([]);
        if (isCustomerGeographicDistributionReport) {
          setDiscountAreas([]);
        }
      } finally {
        if (isActive) {
          setAreasLoading(false);
          setDiscountAreasLoading(false);
        }
      }
    };

    getAreaFilterOptions();

    return () => {
      isActive = false;
    };
  }, [shouldLoadAreas, isCustomerGeographicDistributionReport, direction]);

  useEffect(() => {
    if (!needsCategoryAndDaypartOptions) return;

    let isActive = true;

    const getCategoryAndDaypartOptions = async () => {
      setCategoriesLoading(true);
      setShoppingDaypartsLoading(true);

      try {
        const [categoriesResponse, settingsResponse] = await Promise.all([
          EndPoints.category.getAllCategories({
            light: true,
            perPage: -1,
          }),
          EndPoints.generalSettings.getGeneralSetting(),
        ]);

        if (!isActive) return;

        setCategories(categoriesResponse.data?.data ?? []);
        setShoppingDayparts(
          getShoppingDaypartsValue(settingsResponse.data?.shoppingDayparts),
        );
      } catch (error) {
        if (!isActive) return;

        setCategories([]);
        setShoppingDayparts(getShoppingDaypartsValue());
      } finally {
        if (isActive) {
          setCategoriesLoading(false);
          setShoppingDaypartsLoading(false);
        }
      }
    };

    getCategoryAndDaypartOptions();

    return () => {
      isActive = false;
    };
  }, [needsCategoryAndDaypartOptions, direction]);

  const rfmScoreOptions = [1, 2, 3, 4, 5].map((score) => ({
    label: `${score}`,
    value: score,
  }));

  const rfmActivityStatusOptions = [
    { label: t("rfm_activity_active"), value: "active" },
    { label: t("rfm_activity_dormant"), value: "dormant" },
    { label: t("rfm_activity_new"), value: "new" },
    { label: t("rfm_activity_returning"), value: "returning" },
  ];

  const rfmStoreFilterModeOptions = [
    { label: t("rfm_last_order_store"), value: "last_order_store" },
    {
      label: t("rfm_dominant_store_by_orders"),
      value: "dominant_store_by_orders",
    },
    {
      label: t("rfm_dominant_store_by_value"),
      value: "dominant_store_by_value",
    },
  ];

  const customerSatisfactionStatusOptions = [
    CustomerSatisfactionStatus.Satisfied,
    CustomerSatisfactionStatus.Unsatisfied,
    CustomerSatisfactionStatus.NoResponse,
  ].map((status) => ({
    label: t(getCustomerSatisfactionStatusLabel(status)),
    value: status,
  }));

  const booleanFilterOptions = [
    { label: t("yes"), value: true },
    { label: t("no"), value: false },
  ];

  const fulfillmentStatusOptions = [
    "fully_fulfilled",
    "quantity_shortage",
    "missing_products",
    "mixed",
    "no_products",
  ].map((status) => ({
    label: t(`fulfillment_status_${status}`),
    value: status,
  }));

  const areaSourceOptions = [
    { label: t("area_source_any"), value: "any" },
    { label: t("area_source_primary_address"), value: "primaryAddress" },
    { label: t("area_source_last_paid_order"), value: "lastPaidOrder" },
    {
      label: t("area_source_most_frequent_paid_order"),
      value: "mostFrequentPaidOrder",
    },
  ];

  return (
    <Row gutter={[16, 8]}>
      <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
        <FieldBuilder
          name="type"
          label={`${t("type")}`}
          width="large"
          rules={{ required: true }}
          input={{
            type: "select",
            options: type.map((el) => {
              return {
                label: t(getReportType(el) as string),
                value: el,
              };
            }),
          }}
        />
      </Col>

      {showLongReportRangeNote ? (
        <Col span={24}>
          <Alert
            showIcon
            type="info"
            message={t("long_report_range_async_note")}
          />
        </Col>
      ) : null}

      {isRfm ? (
        <>
          <Col span={24}>
            <Alert
              showIcon
              type="info"
              message={t("store_filter_all_stores_note")}
            />
          </Col>
          <Col span={24}>
            <Alert showIcon type="info" message={t("rfm_report_window")} />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="startDate"
              label={`${t("report_start_date")}`}
              rules={{ required: true, validate: validateNotFutureDate }}
              input={{
                type: "date-picker",
                format: DATE_FORMAT,
                showTime: false,
              }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="endDate"
              label={`${t("report_end_date")}`}
              rules={{
                required: true,
                validate: validateRfmReportEndDate,
              }}
              input={{
                type: "date-picker",
                format: DATE_FORMAT,
                showTime: false,
                disabled: !startDateWatch,
              }}
            />
          </Col>
          <Col span={24}>
            <Alert showIcon type="info" message={t("rfm_history_window")} />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="historyStartDate"
              label={`${t("history_start_date")}`}
              rules={{ validate: validateRfmHistoryStartDate }}
              input={{
                type: "date-picker",
                format: DATE_FORMAT,
                showTime: false,
              }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="historyEndDate"
              label={`${t("history_end_date")}`}
              rules={{ validate: validateRfmHistoryEndDate }}
              input={{
                type: "date-picker",
                format: DATE_FORMAT,
                showTime: false,
              }}
            />
          </Col>
          <Col xs={24} sm={24} md={8} lg={8} xl={8} xxl={8}>
            <FieldBuilder
              name="rScores"
              label={`${t("r_score")}`}
              width="large"
              input={{
                type: "multiSelect",
                options: rfmScoreOptions,
              }}
            />
          </Col>
          <Col xs={24} sm={24} md={8} lg={8} xl={8} xxl={8}>
            <FieldBuilder
              name="fScores"
              label={`${t("f_score")}`}
              width="large"
              input={{
                type: "multiSelect",
                options: rfmScoreOptions,
              }}
            />
          </Col>
          <Col xs={24} sm={24} md={8} lg={8} xl={8} xxl={8}>
            <FieldBuilder
              name="mScores"
              label={`${t("m_score")}`}
              width="large"
              input={{
                type: "multiSelect",
                options: rfmScoreOptions,
              }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="recencyDaysMin"
              label={`${t("recency_days_min")}`}
              width="large"
              rules={{ validate: validateNonNegativeNumber }}
              input={{ type: "number" }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="recencyDaysMax"
              label={`${t("recency_days_max")}`}
              width="large"
              rules={{
                validate: {
                  nonNegative: validateNonNegativeNumber,
                  range: validateMaxNumber(recencyDaysMinWatch),
                },
              }}
              input={{ type: "number" }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="frequencyMin"
              label={`${t("frequency_min")}`}
              width="large"
              rules={{ validate: validateNonNegativeNumber }}
              input={{ type: "number" }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="frequencyMax"
              label={`${t("frequency_max")}`}
              width="large"
              rules={{
                validate: {
                  nonNegative: validateNonNegativeNumber,
                  range: validateMaxNumber(frequencyMinWatch),
                },
              }}
              input={{ type: "number" }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="monetaryMin"
              label={`${t("monetary_min")}`}
              width="large"
              rules={{ validate: validateNonNegativeNumber }}
              input={{ type: "number" }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="monetaryMax"
              label={`${t("monetary_max")}`}
              width="large"
              rules={{
                validate: {
                  nonNegative: validateNonNegativeNumber,
                  range: validateMaxNumber(monetaryMinWatch),
                },
              }}
              input={{ type: "number" }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="activityStatuses"
              label={`${t("activity_statuses")}`}
              width="large"
              input={{
                type: "multiSelect",
                options: rfmActivityStatusOptions,
              }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="storeIds"
              label={`${t("stores")}`}
              width="large"
              input={{
                type: "multiSelect",
                loading: storesLoading,
                allowSearch: true,
                options: stores?.map((store, index) => {
                  return {
                    key: index,
                    label: store?.name,
                    value: store?.id,
                  };
                }),
              }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="storeFilterMode"
              label={`${t("store_filter_mode")}`}
              width="large"
              input={{
                type: "select",
                options: rfmStoreFilterModeOptions,
              }}
            />
          </Col>
        </>
      ) : isCategorySalesTiming ? (
        <>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="startDate"
              label={`${t("start_date")}`}
              rules={{ required: true, validate: validateNotFutureDate }}
              input={{
                type: "date-picker",
                format: DATE_FORMAT,
                showTime: false,
              }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="endDate"
              label={`${t("end_date")}`}
              rules={{
                required: true,
                validate: validateCategorySalesTimingEndDate,
              }}
              input={{
                type: "date-picker",
                format: DATE_FORMAT,
                showTime: false,
                disabled: !startDateWatch,
              }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="storeIds"
              label={`${t("stores")}`}
              width="large"
              input={{
                type: "multiSelect",
                loading: storesLoading,
                allowSearch: true,
                options: stores?.map((store, index) => {
                  return {
                    key: index,
                    label: store?.name,
                    value: store?.id,
                  };
                }),
              }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="categoryIds"
              label={`${t("categories")}`}
              width="large"
              input={{
                type: "multiSelect",
                loading: categoriesLoading,
                allowSearch: true,
                options: categories?.map((category) => ({
                  label: category?.name,
                  value: category?.id,
                })),
              }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="daypartKeys"
              label={`${t("shopping_dayparts")}`}
              width="large"
              input={{
                type: "multiSelect",
                loading: shoppingDaypartsLoading,
                allowSearch: true,
                options: shoppingDayparts.map((daypart) => ({
                  label: `${getShoppingDaypartLabel(daypart, i18n.language)} (${daypart.from} - ${daypart.to})`,
                  value: daypart.key,
                })),
              }}
            />
          </Col>
        </>
      ) : isShoppingCartBehavior ? (
        <>
          <Col span={24}>
            <Alert
              showIcon
              type="info"
              message={t("cart_behavior_report_note")}
            />
          </Col>
          <Col span={24}>
            <Alert
              showIcon
              type="info"
              message={t("store_filter_all_stores_note")}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="startDate"
              label={`${t("start_date")}`}
              rules={{ required: true, validate: validateNotFutureDate }}
              input={{
                type: "date-picker",
                format: DATE_FORMAT,
                showTime: false,
              }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="endDate"
              label={`${t("end_date")}`}
              rules={{
                required: true,
                validate: validateCartBehaviorEndDate,
              }}
              input={{
                type: "date-picker",
                format: DATE_FORMAT,
                showTime: false,
                disabled: !startDateWatch,
              }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="cartValueMin"
              label={`${t("cart_value_min")}`}
              width="large"
              rules={{ validate: validateNonNegativeNumber }}
              input={{ type: "number" }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="cartValueMax"
              label={`${t("cart_value_max")}`}
              width="large"
              rules={{
                validate: {
                  nonNegative: validateNonNegativeNumber,
                  range: validateMaxNumber(cartValueMinWatch),
                },
              }}
              input={{ type: "number" }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="minOrdersPerUser"
              label={`${t("min_orders_per_user")}`}
              width="large"
              rules={{ validate: validateNonNegativeNumber }}
              input={{ type: "number" }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="maxOrdersPerUser"
              label={`${t("max_orders_per_user")}`}
              width="large"
              rules={{
                validate: {
                  nonNegative: validateNonNegativeNumber,
                  range: validateMaxNumber(minOrdersPerUserWatch),
                },
              }}
              input={{ type: "number" }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="deliveryTypes"
              label={`${t("delivery_type")}`}
              width="large"
              input={{
                type: "multiSelect",
                options: [
                  { label: t("express_delivery"), value: "express" },
                  { label: t("normal_delivery"), value: "normal" },
                ],
              }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="storeIds"
              label={`${t("stores")}`}
              width="large"
              input={{
                type: "multiSelect",
                loading: storesLoading,
                allowSearch: true,
                options: stores?.map((store, index) => {
                  return {
                    key: index,
                    label: store?.name,
                    value: store?.id,
                  };
                }),
              }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="categoryIds"
              label={`${t("categories")}`}
              width="large"
              input={{
                type: "multiSelect",
                loading: categoriesLoading,
                allowSearch: true,
                options: categories?.map((category) => ({
                  label: category?.name,
                  value: category?.id,
                })),
              }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="completionMinutesMin"
              label={`${t("completion_minutes_min")}`}
              width="large"
              rules={{ validate: validateNonNegativeNumber }}
              input={{ type: "number" }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="completionMinutesMax"
              label={`${t("completion_minutes_max")}`}
              width="large"
              rules={{
                validate: {
                  nonNegative: validateNonNegativeNumber,
                  range: validateMaxNumber(completionMinutesMinWatch),
                },
              }}
              input={{ type: "number" }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="daypartKeys"
              label={`${t("shopping_dayparts")}`}
              width="large"
              input={{
                type: "multiSelect",
                loading: shoppingDaypartsLoading,
                allowSearch: true,
                options: shoppingDayparts.map((daypart) => ({
                  label: `${getShoppingDaypartLabel(daypart, i18n.language)} (${daypart.from} - ${daypart.to})`,
                  value: daypart.key,
                })),
              }}
            />
          </Col>
        </>
      ) : isCustomerGeographicDistributionReport ? (
        <>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="clientCreatedStartDate"
              label={t("client_created_start_date")}
              rules={{ validate: validateNotFutureDate }}
              input={{
                type: "date-picker",
                format: DATE_FORMAT,
                showTime: false,
              }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="clientCreatedEndDate"
              label={t("client_created_end_date")}
              rules={{
                validate: validateEndDateAfter(clientCreatedStartDateWatch),
              }}
              input={{
                type: "date-picker",
                format: DATE_FORMAT,
                showTime: false,
              }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="orderStartDate"
              label={t("order_start_date")}
              rules={{ validate: validateNotFutureDate }}
              input={{
                type: "date-picker",
                format: DATE_FORMAT,
                showTime: false,
              }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="orderEndDate"
              label={t("order_end_date")}
              rules={{ validate: validateEndDateAfter(orderStartDateWatch) }}
              input={{
                type: "date-picker",
                format: DATE_FORMAT,
                showTime: false,
              }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="coverageAreaIds"
              label={t("coverage_areas")}
              width="large"
              input={{
                type: "multiSelect",
                loading: areasLoading,
                allowSearch: true,
                options: areas.map((area) => ({
                  label: area?.name,
                  value: area?.id,
                })),
              }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="coverageAreaSource"
              label={t("coverage_area_source")}
              width="large"
              input={{
                type: "select",
                options: areaSourceOptions,
              }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="includeOutsideCoverageAreas"
              label={t("include_outside_coverage_areas")}
              width="large"
              input={{ type: "switch" }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="discountAreaIds"
              label={t("discount_areas")}
              width="large"
              input={{
                type: "multiSelect",
                loading: discountAreasLoading,
                allowSearch: true,
                options: discountAreas.map((area) => ({
                  label: area?.name,
                  value: area?.id,
                })),
              }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="discountAreaSource"
              label={t("discount_area_source")}
              width="large"
              input={{
                type: "select",
                options: areaSourceOptions,
              }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="includeOutsideDiscountAreas"
              label={t("include_outside_discount_areas")}
              width="large"
              input={{ type: "switch" }}
            />
          </Col>
        </>
      ) : isCustomerSatisfactionReport ? (
        <>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="startDate"
              label={`${t("start_date")}`}
              rules={{ required: true, validate: validateNotFutureDate }}
              input={{
                type: "date-picker",
                format: DATE_FORMAT,
                showTime: false,
              }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="endDate"
              label={`${t("end_date")}`}
              rules={{
                required: true,
                validate: validateReportEndDate,
              }}
              input={{
                type: "date-picker",
                format: DATE_FORMAT,
                showTime: false,
                disabled: !startDateWatch,
              }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="storeIds"
              label={`${t("stores")}`}
              width="large"
              input={{
                type: "multiSelect",
                loading: storesLoading,
                allowSearch: true,
                options: stores?.map((store, index) => ({
                  key: index,
                  label: store?.name,
                  value: store?.id,
                })),
              }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="areaIds"
              label={`${t("areas")}`}
              width="large"
              input={{
                type: "multiSelect",
                loading: areasLoading,
                allowSearch: true,
                options: areas.map((area, index) => ({
                  key: index,
                  label: area?.name,
                  value: area?.id,
                })),
              }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="satisfactionStatuses"
              label={`${t("customer_satisfaction_status")}`}
              width="large"
              input={{
                type: "multiSelect",
                options: customerSatisfactionStatusOptions,
              }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="reasonIds"
              label={`${t("customer_dissatisfaction_reasons")}`}
              width="large"
              input={{
                type: "multiSelect",
                loading: dissatisfactionReasonsLoading,
                allowSearch: true,
                options: dissatisfactionReasons.map((reason) => ({
                  label: reason?.reason,
                  value: reason?.id,
                })),
              }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="driverIds"
              label={`${t("delivery_worker")}`}
              width="large"
              input={{
                type: "multiSelect",
                loading: deliveryWorkersLoading,
                allowSearch: true,
                options: deliveryWorkers?.map((user, index) => ({
                  key: index,
                  label:
                    `${user?.firstName ?? ""} ${user?.lastName ?? ""}`.trim() ||
                    user?.fullName ||
                    user?.name ||
                    `#${user?.id}`,
                  value: user?.id,
                })),
              }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="hasDiscountCode"
              label={`${t("has_discount_code")}`}
              width="large"
              input={{
                type: "select",
                options: booleanFilterOptions,
              }}
            />
          </Col>
          {hasDiscountCodeWatch !== false ? (
            <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
              <FieldBuilder
                name="discountCodeIds"
                label={`${t("discount_codes")}`}
                width="large"
                input={{
                  type: "multiSelect",
                  loading: discountCodesLoading,
                  allowSearch: true,
                  options: discountCodes?.map((discountCode, index) => ({
                    key: index,
                    label: discountCode?.code
                      ? `${discountCode.code}${
                          discountCode?.name ? ` - ${discountCode.name}` : ""
                        }`
                      : discountCode?.name,
                    value: discountCode?.id,
                  })),
                }}
              />
            </Col>
          ) : null}
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="hasFreeDelivery"
              label={`${t("has_free_delivery")}`}
              width="large"
              input={{
                type: "select",
                options: booleanFilterOptions,
              }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="fulfillmentStatus"
              label={`${t("fulfillment_status")}`}
              width="large"
              input={{
                type: "select",
                options: fulfillmentStatusOptions,
              }}
            />
          </Col>
        </>
      ) : isCouponImpact ? (
        <>
          <Col span={24}>
            <FieldBuilder
              name="couponSelectMode"
              label={t("coupon_select_mode")}
              width="large"
              rules={{ required: true }}
              input={{
                type: "radio",
                options: [
                  {
                    label: t("coupon_select_from_list"),
                    value: "select",
                  },
                  {
                    label: t("coupon_enter_manually"),
                    value: "manual",
                  },
                ],
              }}
            />
          </Col>
          {couponSelectModeWatch === "manual" ? (
            <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
              <FieldBuilder
                name="couponCode"
                label={`${t("code")}`}
                width="large"
                rules={{ required: true }}
                input={{ type: "text" }}
              />
            </Col>
          ) : (
            <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
              <FieldBuilder
                name="discountCodeId"
                label={`${t("discount_code")}`}
                width="large"
                rules={{ required: true }}
                input={{
                  type: "select",
                  loading: discountCodesLoading,
                  allowSearch: true,
                  options: discountCodes?.map((discountCode, index) => {
                    return {
                      key: index,
                      label: discountCode?.code
                        ? `${discountCode.code}${
                            discountCode?.name ? ` - ${discountCode.name}` : ""
                          }`
                        : discountCode?.name,
                      value: discountCode?.id,
                    };
                  }),
                }}
              />
            </Col>
          )}
        </>
      ) : isLowStock ? (
        <>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="store_id"
              label={`${t("store")}`}
              width="large"
              rules={{ required: true }}
              input={{
                type: "select",
                loading: storesLoading,
                options: stores?.map((store, index) => {
                  return {
                    key: index,
                    label: store?.name,
                    value: store?.id,
                  };
                }),
              }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="threshold"
              label={`${t("threshold")}`}
              width="large"
              rules={{ required: true, min: 0 }}
              input={{ type: "number" }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="startDate"
              label={`${t("start_date")}`}
              rules={{ validate: validateNotFutureDate }}
              input={{
                type: "date-picker",
                format: DATE_FORMAT,
                showTime: false,
              }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="endDate"
              label={`${t("end_date")}`}
              rules={{ validate: validateReportEndDate }}
              input={{
                type: "date-picker",
                format: DATE_FORMAT,
                showTime: false,
                disabled: !startDateWatch,
              }}
            />
          </Col>
        </>
      ) : isProductBatchStock ? (
        <>
          <Col span={24}>
            <FieldBuilder
              name="batchStockFilterMode"
              label={t("batch_stock_filter_heading")}
              width="large"
              rules={{ required: true }}
              input={{
                type: "radio",
                options: [
                  {
                    label: t("batch_stock_filter_expiration"),
                    value: "expiration",
                  },
                  {
                    label: t("batch_stock_filter_remaining"),
                    value: "remaining",
                  },
                ],
              }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="store_id"
              label={`${t("store")}`}
              width="large"
              input={{
                type: "select",
                loading: storesLoading,
                allowSearch: true,
                options: stores?.map((store, index) => {
                  return {
                    key: index,
                    label: store?.name,
                    value: store?.id,
                  };
                }),
              }}
            />
          </Col>
          {batchStockFilterModeWatch === "remaining" ? (
            <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
              <FieldBuilder
                name="remaining_days"
                label={`${t("remaining_days_until_expiry")}`}
                width="large"
                rules={{ required: true, min: 0 }}
                input={{ type: "number" }}
              />
            </Col>
          ) : (
            <>
              <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
                <FieldBuilder
                  name="startDate"
                  label={`${t("start_date")}`}
                  rules={{ validate: validateNotFutureDate }}
                  input={{
                    type: "date-picker",
                    format: DATE_FORMAT,
                    showTime: false,
                  }}
                />
              </Col>
              <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
                <FieldBuilder
                  name="endDate"
                  label={`${t("end_date")}`}
                  rules={{ validate: validateReportEndDate }}
                  input={{
                    type: "date-picker",
                    format: DATE_FORMAT,
                    showTime: false,
                  }}
                />
              </Col>
            </>
          )}
        </>
      ) : isProductMargin ? (
        <>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="store_id"
              label={`${t("store")}`}
              width="large"
              input={{
                type: "select",
                loading: storesLoading,
                allowSearch: true,
                options: stores?.map((store, index) => {
                  return {
                    key: index,
                    label: store?.name,
                    value: store?.id,
                  };
                }),
              }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="trademark_id"
              label={`${t("trademark")}`}
              width="large"
              input={{
                type: "select",
                loading: trademarksLoading,
                allowSearch: true,
                options: trademarks?.map((trademark, index) => {
                  return {
                    key: index,
                    label: trademark?.name,
                    value: trademark?.id,
                  };
                }),
              }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="product_status"
              label={`${t("status")}`}
              width="large"
              input={{
                type: "select",
                options: [1, 2].map((status) => {
                  return {
                    label: t(getProductStatus(status) as string),
                    value: status,
                  };
                }),
              }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="store_quantity_status"
              label={`${t("store_status")}`}
              width="large"
              input={{
                type: "select",
                options: [1, 2].map((status) => {
                  return {
                    label: t(getProductStatus(status) as string),
                    value: status,
                  };
                }),
              }}
            />
          </Col>
        </>
      ) : (
        <>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="startDate"
              label={`${t("start_date")}`}
              rules={{ required: true, validate: validateNotFutureDate }}
              input={{
                type: "date-picker",
                format: DATE_FORMAT,
                showTime: false,
              }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="endDate"
              label={`${t("end_date")}`}
              rules={{
                required: true,
                validate: validateReportEndDate,
              }}
              input={{
                type: "date-picker",
                format: DATE_FORMAT,
                showTime: false,
              }}
            />
          </Col>
        </>
      )}

      {typeWatch === ReportType.DRIVER_PAYMENTS ? (
        <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
          <FieldBuilder
            name="driverId"
            label={`${t("delivery_worker")}`}
            width="large"
            rules={{ required: true }}
            input={{
              type: "select",
              loading: deliveryWorkersLoading,
              options: deliveryWorkers?.map((user, index) => {
                return {
                  key: index,
                  label: user?.firstName + " " + user?.lastName,
                  value: user?.id,
                };
              }),
            }}
          />
        </Col>
      ) : typeWatch === ReportType.USER_ACTIVITY_REPORT ? (
        <>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="min_orders"
              label={`${t("min_orders")}`}
              width="large"
              rules={{ min: 0 }}
              input={{ type: "number" }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="max_orders"
              label={`${t("max_orders")}`}
              width="large"
              rules={{ min: 0 }}
              input={{ type: "number" }}
            />
          </Col>
        </>
      ) : typeWatch === ReportType.MONTHLY_USER_ACTIVITY_REPORT ? (
        <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
          <FieldBuilder
            name="min_orders_per_month"
            label={`${t("min_orders_per_month")}`}
            width="large"
            rules={{ required: true, min: 1 }}
            input={{ type: "number" }}
          />
        </Col>
      ) : null}
    </Row>
  );
};

const ReportForm = () => {
  return <Form />;
};

export default ReportForm;
